import apiClient from './client';
import { getDrugLabAnonToken } from '../utils/authStorage';

// Drug Lab — pick any drug, get a branching playable map (SageRx canonical substrate).

export async function searchDrugs(q, limit = 20) {
  const { data } = await apiClient.get('/api/drug-lab/search', { params: { q, limit } });
  return data; // [{ rxcui, name, matched_brand, authored }]
}

export async function getDrugMap(rxcui) {
  const { data } = await apiClient.get(`/api/drug-lab/${rxcui}/map`);
  return data; // { nodes, edges, grid_cols, grid_rows, layout, archetype, ... }
}

// The default comprehensive drug graph (clustered web + portals). region='home'
// is the curated high-yield landing view; any other value is an ATC class prefix.
export async function getDrugGraph(region = 'home', examId = null, deckIds = null) {
  const params = { region };
  // deck_ids (e.g. a Reckoning's decks) scopes the reticle to those decks and
  // takes precedence over exam_id; otherwise exam_id scopes to the selected exam.
  const deckIdsParam = Array.isArray(deckIds) ? deckIds.filter(Boolean).join(',') : (deckIds || '');
  if (deckIdsParam) params.deck_ids = deckIdsParam;
  else if (examId) params.exam_id = examId; // scopes the upload-match reticle to the selected exam's drugs
  const { data } = await apiClient.get('/api/drug-lab/graph', { params });
  return data; // { layout:'radial', region, title, nodes (drug|portal), edges, fixedWidth, fixedHeight }
}

// Start a v2 batch-combat encounter for a drug-map node. Returns a session
// payload shaped like the harness /init (carries `blocks` + `batch_size`) so
// BatchBossSession can hydrate directly from it via sessionIdOverride.
export async function startNodeCombat(rxcui, nodeRole) {
  const { data } = await apiClient.post(
    `/api/drug-lab/${rxcui}/node/${encodeURIComponent(nodeRole)}/combat/start`,
    {},
  );
  return data; // { session_id, blocks, batch_size, boss_name, boss_hp, ... }
}

// Mint a per-browser ephemeral anonymous demo session so a logged-out landing
// visitor can play the warfarin Drug-Lab map. Public endpoint (no auth needed).
// Returns { access_token, token_type, user_id, username, expires_in }.
//
// Pass an optional `demoSlug` (e.g. 'hypertension') to mint a curated shareable-demo
// session: the backend skips the random anon loadout (leaving augment slots free
// for the loadout draft), scopes the session to the demo's allow-listed rxcui(s),
// and issues a shorter-lived token. Absent/unknown slug → unchanged behavior.
// Carries the CURRENT anon token as `prior_token` when one exists, so this path gets
// the same identity continuity the 401 interceptor does (2026-07-27): the server
// refreshes that same user instead of handing back a new user_id that owns none of the
// run's rows. Without it "the client sends prior_token" would only be true of the
// interceptor, and the resume-an-expired-run path here would still orphan the session.
export async function mintAnonDemoToken(demoSlug = null) {
  const body = demoSlug ? { demo_slug: demoSlug } : {};
  const priorToken = getDrugLabAnonToken();
  if (priorToken) body.prior_token = priorToken;
  const { data } = await apiClient.post('/api/auth/anon-demo', body);
  return data;
}

export async function claimArcadeIdentity(arcadeTag, demoSlug) {
  const { data } = await apiClient.post('/api/arcade/claim', {
    arcade_tag: arcadeTag,
    demo_slug: demoSlug,
  });
  return data;
}

export async function attachArcadeIdentity(arcadeTag, demoSlug) {
  const { data } = await apiClient.post('/api/arcade/attach', {
    arcade_tag: arcadeTag,
    demo_slug: demoSlug,
  });
  return data;
}

export async function resumeArcadeIdentity(arcadeTag) {
  const { data } = await apiClient.post('/api/arcade/resume', { arcade_tag: arcadeTag });
  return data;
}

// Dev-only permanent no-login session for the seeded _dev_druglab user, so jon
// lands on the REAL dashboard/Drug-Lab map without a signup/login. Backed by the
// env-gated POST /api/auth/dev-login (api/dev_auth.py) — it 404s unless the
// backend has DEBONO_DEV_AUTH=1, so this is inert on every non-dev env.
// Returns { access_token, token_type, user_id, username }.
export async function devPanelLogin() {
  const { data } = await apiClient.post('/api/auth/dev-login');
  return data;
}
